import type {
  Annotation,
  DeprecatedPlanDim,
  Detail,
  LinearDimension,
  Linework,
  Note,
} from "building-types/annotation/annotationTypes";
import {
  AnnotationType,
  PlacementXAnchor,
  PlacementYAnchor,
  TextAlignment,
} from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type { Dimension } from "building-types/dimensions/dimensionTypes";
import type { Asset } from "building-types/entities/asset";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ParameterValue } from "building-types/options/parameter";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import {
  SheetGeometryCategory,
  annotationAllLvlDimensionCategoryReplaceMap,
  annotationDimensionCategoryReplaceMap,
} from "building-types/views/sheetGeometryCategory";
import type {
  NoteLeaderArrowProps,
  SheetGeometry,
  SheetGeometryList,
  SheetPolylineGeometry,
  SheetTextGeometry,
} from "building-types/views/sheetGeometryTypes";
import {
  sheetAddGeometries,
  sheetAddGeometry,
  sheetGeometryListNew,
  SheetGeometryType,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { DimensionPrecision } from "building-types/views/viewTypes";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import type { Table } from "common/data/table";
import type { SheetPoints } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import degreeToRadian from "common/utils/degreeToRadian";
import { percentNew } from "common/utils/nominalNumbers";
import { aabb2Grow } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import {
  polyLine2AABB2,
  polyLine2Arrow,
  polyLine2ByTwoPoints,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2IsAxisAlignedEdge,
  polyLine2Move,
  polyLine2Rotate,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween, vec2Sub } from "geom/vec2";
import {
  dimensionNew,
  getDimLabelPosition,
  getLinearDimensionLabel,
  getOrientedDimLine,
  makeDimension,
} from "../dimensions";
import { evaluateAnnotationParameterRefs } from "../parameterAnnotation";
import { makeStyledAnnotationLinework } from "../pdf/renderPDFPlanAnnotation";
import type { AnnotationLineworkOutput } from "../pdf/renderPDFPlanAnnotation";
import { isGeometryRef } from "../roofSystemTools";
import { BuildingParameters } from "../systemParameters";
import type { LeaderDefinition } from "../utils/annotation";
import { linearDimensionToPolyLine2sWithTicks, noteLeaderToPolyLine2 } from "../utils/annotation";
import { resolveAnnotationParameterContext, type AnnotationParameterContext } from "./annotationParameterContext";
import { makeDetailAnnotationSheetGeometry } from "./detailAnnotationGeometry";
import { CollisionType, planColliderObstacleNew } from "./interiorLabelObstacle";
import { makeSheetGeometryFromDimensions } from "./planDimensions";
import { PlanGeometryBuilder, type Obstacles } from "./planGeometrySource";
import type { GroupOfTaggedPlanGeometries, PlanGeometryGeneratorResult } from "./planGeometrySourceTypes";
import { PLAN_VIEW_MANUAL_ANNOT_SIZE } from "./planStyleSheets";
import { applyTextGeometryDefaultFontSize } from "./primitives/primitiveStyles";
import { normalizeTextRotation, planTextBoxPerimeter, textGeometryInBuiltInView } from "./primitives/textPrimitive";
import sheetGeometryListAABB2, { allSheetGeometryTypeAABB2Filter } from "./sheetGeometryAABB2";

export function collectAnnotationDimObstacles(o: Obstacles, taggedGeometryGroup: GroupOfTaggedPlanGeometries) {
  let orientedDimLine: PolyLine2 | null = null;
  let witnesses: PolyLine2[] = [];
  for (const taggedGeometry of taggedGeometryGroup.geometries) {
    const { category, type } = taggedGeometry;
    // The group still groups TaggedGeometry-es that were created by dimensionProcessTaggedGeometries
    // therefore have the DIM_* tags not the ANNOT_DIM_* tags.
    switch (category) {
      case SheetGeometryCategory.DIM_LABEL: {
        if (type !== SheetGeometryType.TEXT) {
          break;
        }
        if (!o.obstacles) {
          break;
        }
        const perimeter = planTextBoxPerimeter(textGeometryInBuiltInView(taggedGeometry));
        o.obstacles.push(planColliderObstacleNew(CollisionType.DimAnnotation, perimeter));
        break;
      }
      case SheetGeometryCategory.DIM_LINE: {
        if (type !== SheetGeometryType.POLYLINE) {
          break;
        }
        orientedDimLine = taggedGeometry.paths[0];
        if (polyLine2IsAxisAlignedEdge(orientedDimLine, 0)) {
          o.obstacles?.push(planColliderObstacleNew(CollisionType.DimLine, orientedDimLine));

          if (o.aabbObstacles && polyLine2EdgeCount(orientedDimLine) === 1) {
            const aabb = polyLine2AABB2(orientedDimLine);
            aabb2Grow(aabb, 1, 1);
            o.aabbObstacles.push(aabb);
          } else {
            o.polylineObstacles?.push(orientedDimLine);
          }
        } else {
          o.polylineObstacles?.push(orientedDimLine);
        }
        break;
      }
      case SheetGeometryCategory.DIM_WITNESS: {
        if (taggedGeometry.type !== SheetGeometryType.POLYLINE) {
          break;
        }
        witnesses = taggedGeometry.paths;
        break;
      }
      default: {
        break;
      }
    }
  }
  if (o.obstacles && orientedDimLine && witnesses.length && polyLine2IsAxisAlignedEdge(orientedDimLine, 0)) {
    for (const witnessPl of witnesses) {
      o.obstacles.push(planColliderObstacleNew(CollisionType.DimLine, witnessPl));
    }
  }
}

function annotationLineworkObstacles(o: Obstacles, linework: SheetPolylineGeometry) {
  if (!o.obstacles) {
    return;
  }
  // obstacle
  for (const path of linework.paths) {
    for (let i = 0; i < polyLine2EdgeCount(path); i++) {
      if (polyLine2EdgeLength(path, i) > 36) {
        continue;
      }
      const edge = polyLine2Edge(path, i);
      o.obstacles.push(planColliderObstacleNew(CollisionType.DimLine, edge));
    }
  }
}

function annotationNoteObstacles(o: Obstacles, note: SheetTextGeometry) {
  // obstacle
  const perimeter = planTextBoxPerimeter(textGeometryInBuiltInView(note));
  o.obstacles?.push(planColliderObstacleNew(CollisionType.DimAnnotation, perimeter));
  o.aabbObstacles?.push(polyLine2AABB2(perimeter));
}

export function collectAnnotationObstacles(o: Obstacles, sheetGeometry: SheetGeometry) {
  switch (sheetGeometry.type) {
    case SheetGeometryType.POLYLINE: {
      annotationLineworkObstacles(o, sheetGeometry);
      break;
    }
    case SheetGeometryType.TEXT: {
      annotationNoteObstacles(o, sheetGeometry);
      break;
    }
    default: {
      break;
    }
  }
}

const categoryOrder = [
  SheetGeometryCategory.ANNOT_DIM_LABEL,
  SheetGeometryCategory.ANNOT_DIM_LINE,
  SheetGeometryCategory.ANNOT_NOTE,
  SheetGeometryCategory.ANNOT_LINEWORK,
  SheetGeometryCategory.ANNOT_DETAIL,
  SheetGeometryCategory.ANNOT_DIM_LINEWORK,
  SheetGeometryCategory.ANNOT_DIM_WITNESS,
  SheetGeometryCategory.ANNOT_DIM_TICK,
];

interface AnnotationOptions {
  color?: string;
  annotationNoteLeaderArrowProps?: NoteLeaderArrowProps;
  includeAllLevels?: boolean;
  dimensionPrecision?: DimensionPrecision;
  planRotationRads?: number;
}

interface PlanAddAnnotationProps extends AnnotationOptions, Obstacles {
  annotation: ArrayLike<Annotation> | Table<Annotation>;
  assetCatalog: Table<Asset>;
}

export function planAddAnnotation(list: SheetGeometryList, props: PlanAddAnnotationProps) {
  const geometriesAndGroups: PlanGeometryGeneratorResult = makeSheetGeometryFromAnnotations({
    annotation: props.annotation,
    assetCatalog: props.assetCatalog,
    annotationOptions: props,
  });
  for (const category of categoryOrder) {
    const geometries = geometriesAndGroups.taggedGeometries[category];
    if (!geometries) {
      continue;
    }
    for (const geometry of geometries) {
      sheetAddGeometry(list, geometry);
      collectAnnotationObstacles(props, geometry);
    }
  }
  for (const groupTag in geometriesAndGroups.groups ?? {}) {
    assertIsNotUndefined(geometriesAndGroups.groups);
    const groups: GroupOfTaggedPlanGeometries[] = geometriesAndGroups.groups[groupTag];
    for (const group of groups) {
      collectAnnotationDimObstacles(props, group);
    }
  }
}

export interface PlanLevelAnnotationProps extends Omit<PlanAddAnnotationProps, "annotation"> {
  building: Building;
  levelId: string;
  annotationFilter?: (a: Annotation) => boolean | undefined;
}

export function makeLevelPlanAnnotationGeometry(props: PlanLevelAnnotationProps): SheetGeometry[] {
  const sheetGeometries: SheetGeometry[] = [];
  const geometriesAndGroups: PlanGeometryGeneratorResult = makeSheetGeometryFromLevelPlanAnnotations({
    ...props,
    annotationOptions: props,
    includeAllLevels: false,
  });
  for (const category of categoryOrder) {
    const geometries = geometriesAndGroups.taggedGeometries[category];
    if (!geometries) {
      continue;
    }
    sheetGeometries.push(...geometries);
    for (const geometry of geometries) {
      collectAnnotationObstacles(props, geometry);
    }
  }
  for (const groupTag in geometriesAndGroups.groups ?? {}) {
    assertIsNotUndefined(geometriesAndGroups.groups);
    const groups: GroupOfTaggedPlanGeometries[] = geometriesAndGroups.groups[groupTag];
    for (const group of groups) {
      collectAnnotationDimObstacles(props, group);
    }
  }

  return sheetGeometries;
}

export interface SheetGeometryLevelPlanAnnotationProps {
  building: Building;
  selected?: EntitySet<DocEntityHandle>;
  levelId: string;
  includeAllLevels: boolean;
  annotationFilter?: (a: Annotation) => boolean | undefined;
  annotationOptions: AnnotationOptions;
  planRotationRads?: number;
  globalParameterValues?: Table<ParameterValue>;
  annotationParameterContext?: AnnotationParameterContext;
}

export function makeSheetGeometryFromLevelPlanAnnotations(
  props: SheetGeometryLevelPlanAnnotationProps,
): PlanGeometryBuilder {
  const builder = new PlanGeometryBuilder(true);
  const { building, levelId, includeAllLevels, annotationFilter, globalParameterValues } = props;
  const { roomAnnotation, rooms, levelAnnotation } = building;
  const annotationParameterContext =
    props.annotationParameterContext ?? resolveAnnotationParameterContext(building, globalParameterValues ?? {});

  function getAnnotationsForLevel(levelIdInternal: string): void {
    for (const roomId in roomAnnotation) {
      const { level } = rooms[roomId] ?? {};

      if (!level) {
        continue;
      }

      const { id: roomLevelId } = level;

      if (levelIdInternal === roomLevelId) {
        const annotation = annotationFilter ? roomAnnotation[roomId].filter(annotationFilter) : roomAnnotation[roomId];
        makeSheetGeometryFromAnnotations({
          annotationOptions: props.annotationOptions,
          annotation,
          assetCatalog: building.doc.assets,
          builder,
          building,
          annotationParameterContext,
        });
      }
    }

    if (levelAnnotation[levelIdInternal]) {
      const annotation = annotationFilter
        ? levelAnnotation[levelIdInternal].filter(annotationFilter)
        : levelAnnotation[levelIdInternal];
      makeSheetGeometryFromAnnotations({
        annotationOptions: props.annotationOptions,
        annotation,
        assetCatalog: building.doc.assets,
        builder,
        building,
        annotationParameterContext,
      });
    }
  }

  if (includeAllLevels) {
    for (const lvl in building.levels) {
      getAnnotationsForLevel(lvl);
    }
    return builder;
  } else {
    getAnnotationsForLevel(levelId);
    return builder;
  }
}

export interface SheetSectionAnnotationProps {
  building: Building;
  sectionViewId: string;
  dimensionPrecision?: DimensionPrecision;
  globalParameterValues?: Table<ParameterValue>;
  annotationParameterContext?: AnnotationParameterContext;
}

export function makeSheetGeometryFromSectionViewAnnotations(
  props: SheetSectionAnnotationProps,
): PlanGeometryGeneratorResult {
  const { building, sectionViewId, dimensionPrecision, globalParameterValues } = props;
  const { sectionViewAnnotation } = building;
  const annotationParameterContext =
    props.annotationParameterContext ?? resolveAnnotationParameterContext(building, globalParameterValues ?? {});
  return makeSheetGeometryFromAnnotations({
    annotationOptions: { dimensionPrecision },
    assetCatalog: building.doc.assets,
    annotation: sectionViewAnnotation[sectionViewId],
    building,
    annotationParameterContext,
  });
}

export interface SheetElevationAnnotationProps {
  building: Building;
  referenceFrame: ElevationReferenceFrame;
  dimensionPrecision?: DimensionPrecision;
  globalParameterValues?: Table<ParameterValue>;
  annotationParameterContext?: AnnotationParameterContext;
}

export function makeSheetGeometryFromElevationViewAnnotations(
  props: SheetElevationAnnotationProps,
): PlanGeometryGeneratorResult {
  const { building, referenceFrame, dimensionPrecision, globalParameterValues } = props;
  const { elevationDrawingAnnotation } = building;
  const annotationParameterContext =
    props.annotationParameterContext ?? resolveAnnotationParameterContext(building, globalParameterValues ?? {});
  const builder =
    referenceFrame === "FRONT"
      ? makeSheetGeometryFromLevelPlanAnnotations({
          building,
          includeAllLevels: true,
          levelId: "",
          annotationOptions: { dimensionPrecision },
          annotationFilter: (a) => {
            // legacy image type
            return a.type === AnnotationType.DETAIL && a.detailType === "INLINE_IMAGE" && !!a.addToFrontElevation;
          },
          annotationParameterContext,
        })
      : undefined;
  return makeSheetGeometryFromAnnotations({
    annotationOptions: { dimensionPrecision },
    annotation: elevationDrawingAnnotation[referenceFrame],
    assetCatalog: building.doc.assets,
    builder,
    building,
    annotationParameterContext,
  });
}

export interface NoteLeaderProps {
  readonly leaderLinewidth: SheetPoints;
  readonly color: string;
}

const defaultArrow: NoteLeaderArrowProps = {
  width: 2,
  length: 3,
};

interface SheetAnnotationRecursionProps {
  annotation: ArrayLike<Annotation> | Table<Annotation>;
  annotationOptions: AnnotationOptions;
  assetCatalog: Table<Asset>;
  builder?: PlanGeometryBuilder;
  handle?: Annotation["handle"];
  buildingEntity?: Annotation;
  building?: Building;
  globalParameterValues?: Table<ParameterValue>;
  annotationParameterContext?: AnnotationParameterContext;
}

export function makeSheetGeometryFromAnnotations({
  annotation,
  annotationOptions,
  assetCatalog,
  builder,
  handle,
  buildingEntity,
  building,
  globalParameterValues = {},
  annotationParameterContext,
}: SheetAnnotationRecursionProps): PlanGeometryGeneratorResult {
  if (!annotation) {
    return { taggedGeometries: {} };
  }

  if (!builder) {
    builder = new PlanGeometryBuilder(true);
  }

  let paramValues: Table<ParameterValue> | undefined;
  let params = BuildingParameters;
  if (building) {
    // annotationParameterContext takes precedence; globalParameterValues is only used as a
    // fallback when no pre-computed context is provided.
    const resolvedContext =
      annotationParameterContext ?? resolveAnnotationParameterContext(building, globalParameterValues);
    paramValues = resolvedContext.paramValues;
    params = resolvedContext.params;
  }

  for (const annotId in annotation) {
    const annot: Annotation = annotation[annotId];
    const evaluatedAnnotation =
      building && paramValues
        ? evaluateAnnotationParameterRefs(annot, { doc: building.doc, values: { ...paramValues }, params })
        : annot;
    const finalBuildingEntity = buildingEntity ?? evaluatedAnnotation;
    addAnnotation(annotationOptions, builder, evaluatedAnnotation, assetCatalog, handle, finalBuildingEntity);
  }

  return builder;
}

const lockWidth: number = 2;
const lockHeight: number = 2;
const lockShackleRadius: number = 0.75;

function generateWitnessLineLockPolylines(
  originX: number,
  originY: number,
  width: number,
  height: number,
  shackleRadius: number,
  witnessLineDirX: number,
  witnessLineDirY: number,
): PolyLine2[] {
  const pointsRect: [number, number][] = [];

  const rotAngle = vec2AngleBetween(1, 0, witnessLineDirX, witnessLineDirY) + Math.PI / 2;

  const halfWidth = width / 2;
  const halfHeight = height / 2;

  pointsRect.push([-halfWidth, -halfHeight]); // Bottom-left
  pointsRect.push([halfWidth, -halfHeight]); // Bottom-right
  pointsRect.push([halfWidth, halfHeight]); // Top-right
  pointsRect.push([-halfWidth, halfHeight]); // Top-left

  const pointsShackle: [number, number][] = [];

  const numSegments = 20; // Number of segments to represent the semi-circle
  const angleStep = Math.PI / numSegments; // angle step for semi-circle (0 -> 180 degrees)

  for (let i = 0; i <= numSegments; i++) {
    const angle = Math.PI - i * angleStep; // Start at 180 degrees (left side) and go clockwise
    const x = shackleRadius * Math.cos(angle);
    const y = shackleRadius * Math.sin(angle) + halfHeight;
    pointsShackle.push([x, y]);
  }

  // close the polylines by connecting the last point to the first point
  pointsRect.push(pointsRect[0]);
  pointsShackle.push(pointsShackle[0]);

  return [
    polyLine2Move(polyLine2Rotate(polyLine2New(pointsRect.flat()), rotAngle), originX, originY),
    polyLine2Move(polyLine2Rotate(polyLine2New(pointsShackle.flat()), rotAngle), originX, originY),
  ];
}

// utility functions used for rendering single annotation

interface AddDimensionAnnotationProps {
  includeAllLevels: boolean;
  dimensionPrecision?: DimensionPrecision;
  planRotationRads?: number;
  builder: PlanGeometryBuilder;
  dim: LinearDimension | DeprecatedPlanDim;
  handle?: Annotation["handle"];
  buildingEntity?: Annotation;
}

/**
 * Adds an annotation to the sheet geometry builder.
 * @param annot The annotation to add
 * @param handle An optional handle for the annotation
 * @param buildingEntity An optional building entity to associate with the annotation
 */
function addDimensionAnnotation({
  includeAllLevels,
  dimensionPrecision = DimensionPrecision.PRECISION_1_8,
  planRotationRads,
  builder,
  dim,
  handle,
  buildingEntity,
}: AddDimensionAnnotationProps) {
  if (!buildingEntity) {
    buildingEntity = dim;
  }

  const replaceMap = includeAllLevels
    ? annotationAllLvlDimensionCategoryReplaceMap
    : annotationDimensionCategoryReplaceMap;

  if (dim.type === AnnotationType.LINEAR_DIMENSION) {
    const { entityHostedStart, entityHostedEnd, showEntityHostedLock: lockGeometry, fontSize } = dim;
    const { lengthEdges: dimPls, witnesses: witnessPls, ticks: tickPls } = linearDimensionToPolyLine2sWithTicks(dim);
    let entityHostedStartLock: PolyLine2[] = [];
    const { enabled: entityHostedStartEnabled, hostEntity: hostEntityStart } = entityHostedStart ?? {};

    if (
      entityHostedStartEnabled &&
      hostEntityStart &&
      Object.keys(hostEntityStart).length !== 0 &&
      witnessPls[0] &&
      lockGeometry
    ) {
      const [startWitnessDirX, startWitnessDirY] = vec2Sub(
        witnessPls[0][0],
        witnessPls[0][1],
        witnessPls[0][2],
        witnessPls[0][3],
      );
      entityHostedStartLock = generateWitnessLineLockPolylines(
        witnessPls[0][0],
        witnessPls[0][1],
        lockWidth,
        lockHeight,
        lockShackleRadius,
        startWitnessDirX,
        startWitnessDirY,
      );
    }
    let entityHostedEndLock: PolyLine2[] = [];
    const { enabled: entityHostedEndEnabled, hostEntity: hostEntityEnd } = entityHostedEnd ?? {};

    if (
      entityHostedEndEnabled &&
      hostEntityEnd &&
      Object.keys(hostEntityEnd).length !== 0 &&
      witnessPls[1] &&
      lockGeometry
    ) {
      const [endWitnessDirX, endWitnessDirY] = vec2Sub(
        witnessPls[1][0],
        witnessPls[1][1],
        witnessPls[1][2],
        witnessPls[1][3],
      );
      entityHostedEndLock = generateWitnessLineLockPolylines(
        witnessPls[1][0],
        witnessPls[1][1],
        lockWidth,
        lockHeight,
        lockShackleRadius,
        endWitnessDirX,
        endWitnessDirY,
      );
    }
    const dimLine = polyLine2ByTwoPoints(dim.lineStartX, dim.lineStartY, dim.lineEndX, dim.lineEndY);
    const orientedDimLine = getOrientedDimLine(dimLine);
    const dimAngle = vec2AngleBetween(
      1,
      0,
      orientedDimLine[2] - orientedDimLine[0],
      orientedDimLine[3] - orientedDimLine[1],
    );
    const label = getLinearDimensionLabel(dim, true, dimensionPrecision);
    const labelUnstacked = getLinearDimensionLabel(dim, false, dimensionPrecision);
    const [x, y] = getDimLabelPosition(dimLine);

    const dimension: Dimension = dimensionNew({
      baseDimLine: dimLine,
      dimLine,
      label,
      labelUnstacked,
      linework: dimPls,
      witnessPls,
      ticks: tickPls,
      labelMidPtX: x,
      labelMidPtY: y,
      startAnchor: [dim.startX, dim.startY],
      endAnchor: [dim.endX, dim.endY],
    });

    const dimensionSheetGeometries = makeSheetGeometryFromDimensions({
      handle: dim.handle ?? handle,
      fontSize,
      dimensions: [dimension],
      buildingEntity,
      witnessPlsExtensionPolyLine: [entityHostedStartLock, entityHostedEndLock],
      planRotationRads,
    });

    builder.addAllFrom(dimensionSheetGeometries, replaceMap);

    const category = includeAllLevels
      ? SheetGeometryCategory.ALL_LVL_ANNOT_DIM_LABEL
      : SheetGeometryCategory.ANNOT_DIM_LABEL;

    const { flipped } = normalizeTextRotation(dimAngle, planRotationRads ?? 0);

    if (dim.top && dim.top.length > 0) {
      const topText = sheetTextGeometryNew({
        handle: dim.handle ?? handle,
        text: dim.top,
        fontSize,
        x,
        y,
        /**
         * Position the text above the dimension label
         */
        boxVerticalAlignmentPercentageOffset: flipped ? -1.5 : 1.5,
        rotation: dimAngle,
        alignment: TextAlignment.CENTER,
        placementXAnchor: PlacementXAnchor.CENTER,
        placementYAnchor: PlacementYAnchor.CENTER,
        buildingEntity,
      });
      builder.addGeometry(topText, category);
    }

    if (dim.bottom && dim.bottom.length > 0) {
      const bottomText = sheetTextGeometryNew({
        handle: dim.handle ?? handle,
        text: dim.bottom,
        fontSize,
        x,
        y,
        /**
         * Position the text below the dimension line
         */
        boxVerticalAlignmentPercentageOffset: flipped ? 0.5 : -0.5,
        rotation: dimAngle,
        alignment: TextAlignment.CENTER,
        placementXAnchor: PlacementXAnchor.CENTER,
        placementYAnchor: PlacementYAnchor.CENTER,
        buildingEntity,
      });
      builder.addGeometry(bottomText, category);
    }
  } else if (dim.type === AnnotationType.PLAN_DIM) {
    const { polyLine: sourceLine } = dim;

    if (!isGeometryRef(sourceLine)) {
      const [x, y] = getDimLabelPosition(sourceLine);
      const dimensions: Dimension[] = [];

      makeDimension(dimensions, {
        pl: sourceLine,
        labelPosX: x,
        labelPosY: y,
      });

      const dimensionSheetGeometries = makeSheetGeometryFromDimensions({
        handle: dim.handle ?? handle,
        dimensions,
        buildingEntity,
        planRotationRads,
      });
      builder.addAllFrom(dimensionSheetGeometries, replaceMap);
    }
  }
}

function addLineworkAnnotation({
  includeAllLevels,
  color,
  builder,
  annot,
  handle,
  buildingEntity,
}: {
  includeAllLevels: boolean;
  color?: string;
  builder: PlanGeometryBuilder;
  annot: Linework;
  handle?: Annotation["handle"];
  buildingEntity?: Annotation;
}) {
  if (!buildingEntity) {
    buildingEntity = annot;
  }

  const category = includeAllLevels
    ? SheetGeometryCategory.ALL_LVL_ANNOT_LINEWORK
    : SheetGeometryCategory.ANNOT_LINEWORK;

  const lineworkOutput: AnnotationLineworkOutput = makeStyledAnnotationLinework(annot, color);

  lineworkOutput.fills.forEach((x) =>
    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: annot.handle ?? handle,
        buildingEntity,
        ...x,
      }),
      category,
    ),
  );

  lineworkOutput.lines.forEach((x) =>
    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: annot.handle ?? handle,
        buildingEntity,
        ...x,
      }),
      category,
    ),
  );

  lineworkOutput.offsetLines.forEach((x) =>
    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: annot.handle ?? handle,
        buildingEntity,
        ...x,
      }),
      category,
    ),
  );
}

function addNoteAnnotation({
  includeAllLevels,
  annotationNoteLeaderArrowProps = defaultArrow,
  color,
  builder,
  note,
  handle,
  buildingEntity,
}: {
  includeAllLevels: boolean;
  annotationNoteLeaderArrowProps: NoteLeaderArrowProps;
  color?: string;
  builder: PlanGeometryBuilder;
  note: Note;
  handle?: Annotation["handle"];
  buildingEntity?: Annotation;
}) {
  if (!buildingEntity) {
    buildingEntity = note;
  }

  const category = includeAllLevels ? SheetGeometryCategory.ALL_LVL_ANNOT_NOTE : SheetGeometryCategory.ANNOT_NOTE;
  const { x, y, text } = note;

  const hasLeader = typeof note.leaderLineX === "number" && typeof note.leaderLineY === "number";

  const noteTextGeometry = sheetTextGeometryNew({
    handle: note.handle ?? handle,
    text,
    fontSize: note.fontSize,
    x,
    y,
    rotation: degreeToRadian(note.rotation),
    alignment: note.textAlignment,
    verticalAlignment: note.verticalAlignment,
    placementXAnchor: note.placementXAnchor,
    placementYAnchor: note.placementYAnchor,
    buildingEntity,
    boxWidth: note.textBoxWidth,
    boxHeight: note.textBoxHeight,
    anchorPoint: hasLeader ? { x: note.leaderLineX, y: note.leaderLineY } : undefined,
    annotationNoteLeaderArrowProps,
  });

  if (hasLeader) {
    const leaderDefinition: LeaderDefinition = {
      x: note.x,
      y: note.y,
      leaderLineX: note.leaderLineX,
      leaderLineY: note.leaderLineY,
      landingLineX: note.landingLineX,
      landingLineY: note.landingLineY,
      leaderArrowWidth: note.leaderArrowWidth,
      leaderArrowLength: note.leaderArrowLength,
    };
    buildLeader(builder, leaderDefinition, noteTextGeometry, category, color);
  }

  builder.addGeometry(noteTextGeometry, category);
}

export function buildLeader(
  builder: PlanGeometryBuilder,
  leaderDefinition: LeaderDefinition,
  noteGeometry: SheetTextGeometry,
  category: SheetGeometryCategory,
  color?: string,
  includeArrow: boolean = true,
) {
  const leader = noteLeaderToPolyLine2(leaderDefinition);
  if (leader) {
    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle: noteGeometry.handle,
        paths: [leader],
        fill: false,
        dash: false,
        strokeColor: color,
        buildingEntity: noteGeometry.buildingEntity,
        anchorPoint: noteGeometry.anchorPoint, // This is only added to differentiate from polylines that actually need to be relaxed. Leaders don't get relaxed, they get recreated after the note text is relaxed.
      }),
      category,
    );
    if (includeArrow) {
      const { width: arrowWidth, length: arrowLength } = noteGeometry.annotationNoteLeaderArrowProps ?? defaultArrow;
      const arrow = polyLine2Arrow(
        leader,
        leaderDefinition.leaderArrowWidth ?? arrowWidth,
        leaderDefinition.leaderArrowLength ?? arrowLength,
      );

      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: noteGeometry.handle,
          paths: [arrow],
          linewidth: ZERO_POINTS,
          fill: true,
          fillColor: color,
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(0),
          buildingEntity: noteGeometry.buildingEntity,
          anchorPoint: noteGeometry.anchorPoint, // This is only added to differentiate from polylines that actually need to be relaxed. Leaders don't get relaxed, they get recreated after the note text is relaxed.
        }),
        category,
      );
    }
  }
}

function addDetailAnnotation({
  includeAllLevels,
  builder,
  detail,
  assetCatalog,
  handle,
  buildingEntity,
}: {
  includeAllLevels: boolean;
  builder: PlanGeometryBuilder;
  detail: Detail;
  assetCatalog: Table<Asset>;
  handle?: Annotation["handle"];
  buildingEntity?: Annotation;
}) {
  if (!buildingEntity) {
    buildingEntity = detail;
  }

  const category = includeAllLevels ? SheetGeometryCategory.ALL_LVL_ANNOT_DETAIL : SheetGeometryCategory.ANNOT_DETAIL;

  const geometry = makeDetailAnnotationSheetGeometry(detail, assetCatalog, handle, buildingEntity);
  if (geometry) {
    builder.addGeometry(geometry, category);
  }
}

function addAnnotation(
  {
    color,
    annotationNoteLeaderArrowProps = defaultArrow,
    includeAllLevels = false,
    dimensionPrecision = DimensionPrecision.PRECISION_1_8,
    planRotationRads,
  }: AnnotationOptions,
  builder: PlanGeometryBuilder,
  annot: Annotation,
  assetCatalog: Table<Asset>,
  handle?: Annotation["handle"],
  buildingEntity?: Annotation,
) {
  switch (annot.type) {
    case AnnotationType.LINEAR_DIMENSION:
    case AnnotationType.PLAN_DIM:
      addDimensionAnnotation({
        includeAllLevels,
        dimensionPrecision,
        planRotationRads,
        builder,
        dim: annot,
        handle,
        buildingEntity,
      });
      break;
    case AnnotationType.LINEWORK:
      addLineworkAnnotation({ includeAllLevels, color, builder, annot, handle, buildingEntity });
      break;
    case AnnotationType.NOTE:
      addNoteAnnotation({
        includeAllLevels,
        annotationNoteLeaderArrowProps,
        color,
        builder,
        note: annot,
        handle,
        buildingEntity,
      });
      break;
    case AnnotationType.DETAIL:
      addDetailAnnotation({ includeAllLevels, builder, detail: annot, assetCatalog, handle, buildingEntity });
      break;
    default:
      break;
  }
}

export function makeAnnotationGeometry(anno: Annotation, assetCatalog: Table<Asset>): SheetGeometryList {
  const geometryList = sheetGeometryListNew();
  const sheetGeometries = makeSheetGeometryFromAnnotations({
    annotation: [anno],
    assetCatalog,
    annotationOptions: {},
  });
  for (const tag in sheetGeometries.taggedGeometries) {
    sheetAddGeometries(geometryList, sheetGeometries.taggedGeometries[tag]);
  }
  return geometryList;
}

export function makePlanAnnotationAABB2(annotation: Annotation, assetCatalog: Table<Asset>): AABB2 | null {
  let geometry = makeAnnotationGeometry(annotation, assetCatalog);
  geometry = applyTextGeometryDefaultFontSize(geometry, PLAN_VIEW_MANUAL_ANNOT_SIZE);
  const geometryAABB = sheetGeometryListAABB2(geometry, undefined, allSheetGeometryTypeAABB2Filter);

  if (!geometryAABB) {
    return null;
  }

  return geometryAABB;
}
